import { AlertTriangle, MessageSquareDashed, RotateCcw } from 'lucide-react';
import Button from '../Button.jsx';

/**
 * Compact system notice for a chat turn that ended without a visible reply:
 * the run `failed`, or it `completed` without producing any text. Rendered
 * inline in the thread (not as an assistant bubble) with an optional Retry
 * action that re-sends the user's last prompt. Presentational only — all copy
 * is passed in (already localized).
 *
 * @param {object} props
 * @param {{ kind?: 'no_reply'|'failed', code?: string|null }} props.notice — the thread row (`role: 'notice'`).
 * @param {string} [props.noReplyLabel] — text for `kind: 'no_reply'`.
 * @param {string} [props.failedLabel] — text for `kind: 'failed'`.
 * @param {string} [props.retryLabel] — Retry button label.
 * @param {((notice: object) => void)|null} [props.onRetry] — when omitted the Retry button is hidden.
 * @param {boolean} [props.retryDisabled] — e.g. while a send is already in flight.
 * @param {string} [props.maxWidthClass] — width clamp matching sibling bubbles.
 */
const ChatNoticeRow = ({
  notice,
  noReplyLabel = 'No reply was generated.',
  failedLabel = 'Something went wrong while generating the reply.',
  retryLabel = 'Retry',
  onRetry = null,
  retryDisabled = false,
  maxWidthClass = '',
}) => {
  const kind = notice?.kind === 'failed' ? 'failed' : 'no_reply';
  const isFailed = kind === 'failed';
  const Icon = isFailed ? AlertTriangle : MessageSquareDashed;
  const text = isFailed ? failedLabel : noReplyLabel;
  const code = typeof notice?.code === 'string' && notice.code.trim() ? notice.code.trim() : '';

  return (
    <div
      role={isFailed ? 'alert' : 'status'}
      data-notice-kind={kind}
      className={`flex w-full min-w-0 items-center gap-2 rounded-xl border border-dashed px-3 py-2 text-xs ${maxWidthClass} ${
        isFailed
          ? 'border-red-300 bg-red-50/60 text-red-800 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200'
          : 'border-edge bg-surface text-content-secondary'
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 break-words">
        {text}
        {code ? (
          <span className="ml-1 font-mono text-[11px] opacity-70" data-notice-code>
            ({code})
          </span>
        ) : null}
      </span>
      {onRetry ? (
        <Button
          size="sm"
          variant="secondary"
          leftIcon={RotateCcw}
          disabled={retryDisabled}
          className="shrink-0 whitespace-nowrap"
          onClick={() => onRetry(notice)}
        >
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
};

export default ChatNoticeRow;
